import { $httpFhirApi } from '@/common/api/httpFhir.service';
import { getUrlFromCanonical } from '@/common/core';
import { useProfileFlow } from '@/common/workflowEngine/useProfileFlow';
import { ALLERGIES_SCHEMA } from '@/components/questionnaires/widgets/AllergiesWidget.vue';
import { MEDICATIONS_SCHEMA } from '@/components/questionnaires/widgets/MedicationsListWidget.vue';
import { SUBSTANCE_USE_SCHEMA } from '@/components/questionnaires/widgets/SubstanceUseHistoryWidget.vue';
import { TREATMENT_HISTORY_SCHEMA } from '@/components/questionnaires/widgets/TreatmentHistoryWidget.vue';
import { FAMILY_MEMBER_HEALTH_SCHEMA } from '@/components/questionnaires/widgets/FamilyMemberHealthHistoryWidget.vue';

const widgetSchemaMap = {
    allergiesList: ALLERGIES_SCHEMA,
    medicationList: MEDICATIONS_SCHEMA,
    substanceUseList: SUBSTANCE_USE_SCHEMA,
    treatmentHistory: TREATMENT_HISTORY_SCHEMA,
    familyMemberHistory: FAMILY_MEMBER_HEALTH_SCHEMA,
};

function mapItemToSchema(item) {
    if (item.customWidget) {
        const schema = widgetSchemaMap[item.customWidget];
        if (schema) {
            return { ...schema, linkId: item.linkId };
        }
        return { linkId: item.linkId, text: item.text, type: 'widget', widgetType: item.customWidget };
    }

    const entry = { linkId: item.linkId, text: item.text, type: item.type };

    if (item.answerOption?.length) {
        entry.options = item.answerOption.filter((opt) => opt.value?.Coding).map((opt) => ({ value: opt.value.Coding.display || opt.value.Coding.code, code: opt.value.Coding.code }));
    }

    if (item.type === 'choice') {
        const control = item.itemControl?.codeZero;
        const render = item.render ? JSON.parse(item.render) : {};
        entry.multiple = control === 'check-box' || !!render.multiple;
    }

    if (item.enableWhen?.length) {
        entry.enableWhen = {
            behavior: item.enableBehavior || 'all',
            conditions: item.enableWhen.map((rule) => {
                const condition = { question: rule.question, operator: rule.operator };
                if (rule.answerBoolean !== undefined) condition.answer = rule.answerBoolean;
                else if (rule.answerString !== undefined) condition.answer = rule.answerString;
                else if (rule.answerDecimal !== undefined) condition.answer = rule.answerDecimal;
                else if (rule.answerInteger !== undefined) condition.answer = rule.answerInteger;
                else if (rule.answerCoding !== undefined) condition.answer = rule.answerCoding;
                else if (rule.answerDate !== undefined) condition.answer = rule.answerDate;
                return condition;
            }),
        };
    }

    return entry;
}

export function mapQuestionnaireItemsToSchema(items) {
    return items.flatMap((item) => {
        if (item.type === 'group' && item.item) {
            const sectionTitle = item.text || item.bnText || '';
            return item.item
                .filter((child) => child.type !== 'display' || child.customWidget)
                .map((child) => {
                    const mapped = mapItemToSchema(child);
                    mapped.section = sectionTitle;
                    return mapped;
                });
        }
        if (item.type !== 'display' || item.customWidget) {
            return [mapItemToSchema(item)];
        }
        return [];
    });
}

export async function getSchemaByFormUrl(formUrl) {
    const { fullUrl } = getUrlFromCanonical(formUrl);
    if (!fullUrl) return [];
    const statusUrl = fullUrl + '&status=active';
    const response = await $httpFhirApi.get(statusUrl);
    const questionnaire = Array.isArray(response.data) ? response.data[0] : response.data;
    if (!questionnaire?.item) return [];
    return mapQuestionnaireItemsToSchema(questionnaire.item);
}

export async function getAvailableForms() {
    const { getFormQueries } = useProfileFlow();
    const queries = await getFormQueries({ elements: 'title,url,name,version,status' });

    const responses = await Promise.all(
        queries.map((q) => {
            const fetchUrl = q.url || `${q.resourceType}?${q.query}`;
            return $httpFhirApi.get(fetchUrl);
        }),
    );

    const seen = new Set();
    return responses
        .flatMap((res) => (Array.isArray(res.data) ? res.data : []))
        .filter((q) => {
            if (!q.url || seen.has(q.url)) return false;
            seen.add(q.url);
            return true;
        })
        .map((q) => ({ name: q.title || q.name || '', version: q.version || '', url: q.url }));
}

export function initFormFillerApi() {
    if (!window.formFillerApi) {
        window.formFillerApi = {};
    }
    window.formFillerApi.getSchema = async (formUrl) => {
        if (formUrl) {
            return getSchemaByFormUrl(formUrl);
        }
        if (window.formFillerApi._getCurrentFormSchema) {
            return window.formFillerApi._getCurrentFormSchema();
        }
        return [];
    };
    window.formFillerApi.getAvailableForms = getAvailableForms;
}
